import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { Observable } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';
import { SessionStorage } from 'ngx-webstorage';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { SpongeConfigService, SpongeAtsConfig } from './sponge-config.service';

/**
 * Server-prepared OAuth context: opaque state token plus the backend
 * callback URL the ATS must redirect to.
 */
export interface OAuthPreparation {
  state: string;
  redirect_uri: string;
}

/**
 * Generic integration credentials interface
 */
export interface IntegrationCredentials {
  [key: string]: string | number | boolean;
}

/**
 * Connection status for a Sponge ATS integration, as returned by
 * GET /api/sponge/:ats_key/status
 */
export interface SpongeIntegrationStatus {
  connected: boolean;
  legacy: boolean;
  embed_url: string | null;
  webhook_url?: string | null;
  has_webhook_subscription?: boolean;
  list_action_url: string | null;
}

export type SpongeDiagnosticStatus = 'pass' | 'fail' | 'skipped';

/**
 * One ordered connection probe from GET /api/sponge/:ats_key/diagnostics.
 * `detail` explains a pass/fail; `remediation` is the customer-facing fix
 * for a fail/skipped probe.
 */
export interface SpongeDiagnosticProbe {
  key: string;
  label: string;
  status: SpongeDiagnosticStatus;
  detail?: string;
  remediation?: string;
}

export interface SpongeDiagnosticsReport {
  overall: 'pass' | 'warn' | 'fail';
  checked_at: string;
  probes: SpongeDiagnosticProbe[];
}

/**
 * OAuth configuration interface
 */
export interface OAuthConfig {
  hiring_firm_id: string | number;
  redirect_uri: string;
  client_id?: string;
  client_secret?: string;
  [key: string]: any;
}

/**
 * Generic Sponge Integration Service
 * Handles all ATS integrations through the Sponge framework
 */
@Injectable({
  providedIn: 'root'
})
export class SpongeIntegrationService {
  public static readonly DIAGNOSTICS_TIMEOUT_MS = 90_000;

  @SessionStorage() private hiringFirm!: IHiringFirm;

  constructor(
    private readonly http: HttpClient,
    private readonly configService: SpongeConfigService
  ) {}

  /**
   * Authorize an ATS integration
   * For non-OAuth integrations, this completes the setup
   * For OAuth integrations, use generateOAuthLink() instead
   */
  public authorize(atsKey: string, credentials: IntegrationCredentials): Observable<any> {
    return this.http.post<any>(
      `${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/authorize`,
      credentials
    );
  }

  /**
   * Remove an ATS integration
   */
  public delete(atsKey: string): Observable<any> {
    return this.http.delete(`${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}`);
  }

  /**
   * Get the connection status for an ATS integration: whether it's
   * connected, whether that connection is via the legacy (pre-Sponge)
   * integration, and the embeddable tab URL when available.
   */
  public getStatus(atsKey: string): Observable<SpongeIntegrationStatus> {
    return this.http
      .get<{ data: SpongeIntegrationStatus }>(
        `${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/status`
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Run the adapter's connection diagnostics (only for configs that declare
   * `frontend.supports_diagnostics`). The probes call the ATS synchronously
   * and can take up to a minute, so give up client-side before the browser
   * or a proxy does with a nondescript error.
   */
  public runDiagnostics(atsKey: string): Observable<SpongeDiagnosticsReport> {
    return this.http
      .get<{ data: SpongeDiagnosticsReport }>(
        `${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/diagnostics`
      )
      .pipe(
        timeout(SpongeIntegrationService.DIAGNOSTICS_TIMEOUT_MS),
        map((res) => res.data)
      );
  }

  /**
   * Get an entity from the ATS
   */
  public getEntity(atsKey: string, entityType: string, entityId: string): Observable<any> {
    return this.http.get(`${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/entity`, {
      params: {
        entity_type: entityType,
        entity_id: entityId
      }
    });
  }

  /**
   * Load entity from ATS into Vitay
   */
  public loadEntity(atsKey: string, entityType: string, entityId: string): Observable<any> {
    return this.http.post(`${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/load_entity`, {
      entity_type: entityType,
      entity_id: entityId
    });
  }

  /**
   * Upload entity from Vitay to ATS
   */
  public uploadEntity(atsKey: string, entityType: string, entityId: number): Observable<any> {
    return this.http.post(`${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/upload_entity`, {
      params: {
        entity_type: entityType,
        id: entityId
      }
    });
  }

  /**
   * Upload documents for an entity
   */
  public uploadEntityDocuments(
    atsKey: string,
    entityType: string,
    entityId: number,
    documentOptions?: any
  ): Observable<any> {
    const params: any = {
      entity_type: entityType,
      id: entityId,
      ...documentOptions
    };

    return this.http.post(
      `${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/upload_entity_documents`,
      params
    );
  }

  /**
   * Find existing candidate by external entity ID
   */
  public findCandidate(atsKey: string, entityId: string): Observable<any> {
    return this.http.get(`${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/candidate`, {
      params: { entity_id: entityId }
    });
  }

  /**
   * First step of the OAuth2 authorization-code flow: store the client
   * credentials server-side (the client secret must never appear in the
   * browser-visible OAuth URL) and receive the opaque state token plus
   * the backend callback URL.
   */
  public prepareOAuth(atsKey: string, credentials: IntegrationCredentials): Observable<OAuthPreparation> {
    return this.http
      .post<{ data: OAuthPreparation }>(
        `${environment.SERVER_ENDPOINT}/api/sponge/${atsKey}/oauth/prepare`,
        credentials
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Generate OAuth authorization link from the ATS config and the
   * server-prepared state/redirect context
   */
  public generateOAuthLink(
    atsKey: string,
    credentials: OAuthConfig,
    atsConfig: SpongeAtsConfig,
    oauth: OAuthPreparation
  ): string {
    // Get authorization URL from backend config
    // Check multiple possible locations for backwards compatibility
    const authUrl = (atsConfig.authentication as any).endpoints?.authorize ||
                    atsConfig.authentication.oauth?.authorization_url ||
                    atsConfig.frontend?.oauth_flow?.authorization_url;

    if (!authUrl) {
      throw new Error(`OAuth not configured for ${atsKey}`);
    }

    // Get scope from backend config
    const scope = atsConfig.authentication.oauth?.scope || '';

    const params = new URLSearchParams({
      client_id: String(credentials.client_id || ''),
      response_type: atsConfig.authentication.oauth?.response_type || 'code',
      redirect_uri: oauth.redirect_uri,
      state: oauth.state,
      scope: scope
    });

    return `${authUrl}?${params.toString()}`;
  }

  /**
   * Run the OAuth flow: prepare server-side, then navigate the browser
   * to the ATS authorization page. Subscribe to handle preparation errors.
   */
  public initiateOAuth(atsKey: string, credentials: OAuthConfig, atsConfig: SpongeAtsConfig): Observable<string> {
    return this.prepareOAuth(atsKey, credentials).pipe(
      map((oauth) => this.generateOAuthLink(atsKey, credentials, atsConfig, oauth)),
      tap((url) => window.open(url, '_self'))
    );
  }
}
